import { useMemo, useState } from "react";

import { useLogout } from "@/hooks/useLogout";
import { useAuthStore } from "@/store/auth-store";
import {
  usePermissions,
  type PermissionFlags,
} from "@/hooks/usePermissions";
import { useThemeStore, type ThemePreference } from "@/store/theme-store";
import { AuditLogPanel } from "@/components/admin/AuditLogPanel";
import { CreateUserForm } from "@/components/admin/CreateUserForm";
import { DashboardPanel } from "@/components/admin/DashboardPanel";
import { OrganisationPanel } from "@/components/admin/OrganisationPanel";
import { PermissionsMatrix } from "@/components/admin/PermissionsMatrix";
import { MailPanel } from "@/components/admin/MailPanel";
import { StoragePanel } from "@/components/admin/StoragePanel";
import { TestResultsTable } from "@/components/admin/TestResultsTable";
import { TestsPanel } from "@/components/admin/TestsPanel";
import { UserTable } from "@/components/admin/UserTable";
import { SettingsMenu } from "@/components/settings/SettingsMenu";
import { ThemeToggle } from "@/components/ThemeToggle";
import { APP_NAME, APP_VERSION_LABEL } from "@/lib/branding";

type AdminTab =
  | "dashboard"
  | "assessments"
  | "users"
  | "create"
  | "permissions"
  | "audit"
  | "tests"
  | "organisation"
  | "mail"
  | "storage"
  | "appearance";

interface TabSpec {
  key: AdminTab;
  label: string;
  cy: string;
  /**
   * The permission this item needs. Undefined means every signed-in
   * staff member sees it.
   *
   * THIS IS THE WHOLE ACCESS MODEL FOR THE NAV, and it is not a
   * radiologist special-case. A reviewer holds ``canManageTests`` and
   * nothing administrative, so gating each item on the permission its
   * screen actually uses is what hides Staff accounts, Create user,
   * Role permissions, Audit log, Organisation and Mail from them --
   * while an admin, who holds those permissions, keeps everything. It
   * also closes a real gap: the whole list used to render for anyone,
   * so a reviewer could open a screen whose every API call then 403'd.
   */
  needs?: keyof PermissionFlags;
}

const TABS: TabSpec[] = [
  // -- The work: what a reviewer signs in to do --------------------------
  { key: "dashboard", label: "Dashboard", cy: "tab-dashboard" },
  {
    key: "assessments",
    label: "Assessments",
    cy: "tab-assessments",
    needs: "canManageTests",
  },
  { key: "tests", label: "Tests", cy: "tab-tests", needs: "canManageTests" },
  // -- Administration: people and the record ----------------------------
  { key: "users", label: "Staff accounts", cy: "tab-users", needs: "canManageUsers" },
  { key: "create", label: "Create user", cy: "tab-create", needs: "canManageUsers" },
  {
    key: "permissions",
    label: "Role permissions",
    cy: "tab-permissions",
    needs: "canManageUsers",
  },
  { key: "audit", label: "Audit log", cy: "tab-audit", needs: "canManageUsers" },
  // -- Settings ---------------------------------------------------------
  {
    key: "organisation",
    label: "Organisation settings",
    cy: "tab-organisation",
    needs: "canManageOrganisation",
  },
  { key: "mail", label: "Mail", cy: "tab-mail", needs: "canManageOrganisation" },
  {
    key: "storage",
    label: "Storage",
    cy: "tab-storage",
    needs: "canManageOrganisation",
  },
  { key: "appearance", label: "Appearance", cy: "tab-appearance" },
];

interface ThemeOption {
  key: ThemePreference;
  label: string;
  description: string;
  cy: string;
}

const THEME_OPTIONS: ThemeOption[] = [
  {
    key: "light",
    label: "Light",
    description: "Off-white surface, teal accents.",
    cy: "theme-option-light",
  },
  {
    key: "dark",
    label: "Dark",
    description: "Slate surface, brighter teal accents.",
    cy: "theme-option-dark",
  },
  {
    key: "system",
    label: "System",
    description: "Follow your OS preference.",
    cy: "theme-option-system",
  },
];

function AppearancePanel() {
  const preference = useThemeStore((s) => s.preference);
  const setPreference = useThemeStore((s) => s.setPreference);

  return (
    <div
      data-cy="appearance-panel"
      className="max-w-xl rounded-xl border border-border bg-background-card p-5"
    >
      <h2 className="text-base font-semibold text-foreground">Theme</h2>
      <p className="mt-1 text-xs text-foreground-muted">
        Choose how {APP_NAME} looks. The preference is stored in this
        browser only.
      </p>
      <fieldset className="mt-4 flex flex-col gap-2">
        <legend className="sr-only">Theme preference</legend>
        {THEME_OPTIONS.map((opt) => {
          const checked = preference === opt.key;
          return (
            <label
              key={opt.key}
              data-cy={opt.cy}
              className={[
                "flex cursor-pointer items-start gap-3 rounded-lg border px-3 py-2 transition-colors",
                checked
                  ? "border-accent bg-accent-light text-foreground"
                  : "border-border bg-background-card text-foreground hover:bg-table-row-hover",
              ].join(" ")}
            >
              <input
                type="radio"
                name="theme-preference"
                value={opt.key}
                checked={checked}
                onChange={() => setPreference(opt.key)}
                className="mt-1 h-4 w-4 accent-accent"
              />
              <span className="flex flex-col">
                <span className="text-sm font-medium text-foreground">
                  {opt.label}
                </span>
                <span className="text-xs text-foreground-muted">
                  {opt.description}
                </span>
              </span>
            </label>
          );
        })}
      </fieldset>
    </div>
  );
}

export function AdminPage() {
  const me = useAuthStore((s) => s.user);
  const logout = useLogout();
  const perms = usePermissions();

  // The nav a reviewer or admin actually holds, derived from the
  // permission matrix -- never a role name. Appearance and Dashboard
  // have no `needs`, so everyone keeps them.
  const visibleTabs = useMemo(
    () => TABS.filter((t) => !t.needs || perms.has(t.needs)),
    [perms],
  );

  const [tab, setTab] = useState<AdminTab>("dashboard");
  // Which status the Assessments view opens on, so a Dashboard tile can
  // deep-link into "Awaiting valuation" and land on the filtered list.
  const [assessmentsFilter, setAssessmentsFilter] = useState("all");

  // If the selected tab is not one this user can see -- which cannot
  // happen from clicking, but could from a stale default after a role
  // change -- fall back to the first visible one.
  const activeTab = visibleTabs.some((t) => t.key === tab)
    ? tab
    : (visibleTabs[0]?.key ?? "dashboard");

  const openAssessments = (statusFilter: string) => {
    setAssessmentsFilter(statusFilter);
    setTab("assessments");
  };

  return (
    <main className="flex min-h-screen bg-background text-foreground" data-cy="admin-page">
      {/* Sidebar — same chrome as WorklistPage so the admin surface
          feels integrated, not bolted on.

          NO `white` AND NO ALPHA ON `nav-text` HERE. Both were written
          when this chrome was dark slate-blue, where they were fine:
          `--nav-text` sat around 13:1 against the background, so
          spending 30% of it on alpha still left ~6:1, and a white rule
          read as a highlight. The reference palette made the chrome
          LIGHT. `--nav-text` is now 4.71:1 — over the line by a hair,
          with nothing left to spend — so `/70` and `/80` fell to 2.71
          and 3.23, and every white border went invisible, including the
          3px marker on the ACTIVE tab. Measured 2026-08-22. */}
      <aside
        data-cy="sidebar"
        className="flex w-60 flex-col bg-background-nav text-nav-text"
      >
        <div className="border-b border-border px-5 py-5">
          <div className="text-base font-semibold tracking-tight text-nav-text-active">
            {APP_VERSION_LABEL}
          </div>
          <div className="text-[11px] text-nav-text">Admin console</div>
        </div>
        <nav className="flex flex-col gap-0.5 px-3 py-3">
          {/* No "back to the worklist" link: there is no worklist. In
              the clinic system the console was a detour off the clinical
              surface; here it IS the surface. */}
          {visibleTabs.map((t) => (
            <button
              key={t.key}
              type="button"
              data-cy={t.cy}
              onClick={() => setTab(t.key)}
              className={[
                "rounded-md px-3 py-2 text-left text-sm transition-colors",
                activeTab === t.key
                  ? "border-l-[3px] border-nav-text-active bg-nav-hover text-nav-text-active"
                  : "text-nav-text hover:bg-nav-hover/70 hover:text-nav-text-active",
              ].join(" ")}
            >
              {t.label}
            </button>
          ))}
        </nav>
        <div className="mt-auto border-t border-border px-5 py-4 text-[11px] text-nav-text">
          {me ? (
            <>
              Signed in as
              <div className="mt-0.5 font-mono text-nav-text-active">{me.username}</div>
              <div className="text-[10px]">{me.role}</div>
            </>
          ) : (
            <span>Not signed in</span>
          )}
        </div>
      </aside>

      {/* Content */}
      <section className="flex flex-1 flex-col overflow-hidden">
        <header
          data-cy="topbar"
          className="flex items-center justify-between bg-background-topbar px-6 py-4 text-foreground-on-chrome shadow-theme-sm"
        >
          <div>
            <h1 className="text-xl font-semibold text-foreground-on-chrome">
              {visibleTabs.find((t) => t.key === activeTab)?.label}
            </h1>
            <p className="text-xs text-foreground-on-chrome/80">
              {activeTab === "dashboard" && "Where every sitting is in the process."}
              {activeTab === "assessments" &&
                "Every sitting — filter by status, review and score."}
              {activeTab === "users" && "Staff accounts and candidates."}
              {activeTab === "create" && "Add a candidate, a reviewer or an admin."}
              {activeTab === "organisation" &&
                "The practice's name and logo, as a candidate sees them."}
              {activeTab === "permissions" &&
                "Read-only view of which roles hold which permissions."}
              {activeTab === "audit" &&
                "Hash-chained record of authentication events."}
              {activeTab === "tests" &&
                "Build exams and manage the case library."}
            {activeTab === "mail" &&
              "Outbound email — the Mailgun account, and who hears when a candidate finishes."}
            {activeTab === "storage" &&
              "Where case images and recordings are written — the bucket, and whether it can be reached."}
            {activeTab === "appearance" && "Switch between light and dark themes."}
            </p>
          </div>
          <div className="ml-auto flex items-center gap-3">
            <ThemeToggle />
            {/* Account settings (all users) + System settings (admins) —
                same gear menu as the worklist topbar. The "Admin console"
                item is hidden here since this IS that page. */}
            <SettingsMenu hideAdminConsole />
            <button
              type="button"
              data-cy="btn-logout"
              onClick={() => {
                void logout();
              }}
              className="rounded-md bg-background-card px-3 py-1.5 text-xs font-semibold text-accent hover:bg-accent-light"
            >
              Sign out
            </button>
          </div>
        </header>

        <div className="flex-1 overflow-auto px-6 py-5">
          {activeTab === "dashboard" && (
            <DashboardPanel onOpenAssessments={openAssessments} />
          )}
          {activeTab === "assessments" && (
            // Keyed on the filter so a Dashboard tile that changes it
            // re-opens the list on the chosen status rather than keeping
            // the previous one.
            <TestResultsTable
              key={assessmentsFilter}
              initialStatus={assessmentsFilter}
            />
          )}
          {activeTab === "users" && <UserTable />}
          {activeTab === "create" && <CreateUserForm />}
          {activeTab === "organisation" && <OrganisationPanel />}
          {activeTab === "permissions" && <PermissionsMatrix />}
          {activeTab === "audit" && <AuditLogPanel />}
          {activeTab === "tests" && <TestsPanel />}
          {activeTab === "mail" && <MailPanel />}
          {activeTab === "storage" && <StoragePanel />}
          {activeTab === "appearance" && <AppearancePanel />}
        </div>
      </section>
    </main>
  );
}
